/**
 * Game Initialization Service
 *
 * SINGLE SOURCE OF TRUTH for player/game initialization.
 * All reset flows must go through this service.
 */

import { PlayerStateManager } from '../utils/playerState';
import { DATABASE_SERVICE_URL } from './workerUrls';

export interface InitializationResult {
  success: boolean;
  playerId?: string;
  shipId?: string;
  error?: string;
}

/**
 * Initialize a new player with fresh UUIDs
 * This is the ONLY function that should create new players
 * @param displayName - Optional custom display name for the player
 */
export async function initializeNewPlayer(displayName?: string): Promise<InitializationResult> {
  try {
    const baseUrl = DATABASE_SERVICE_URL();

    // Generate new UUIDs for player and ship
    const { playerId, shipId } = PlayerStateManager.generateNewIds();
    console.log('[INIT] Generated new IDs:', { playerId, shipId });

    // Call backend to create player and ship
    const response = await fetch(`${baseUrl}/admin/initialize-player`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ playerId, shipId, displayName }),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ error: response.statusText }));
      throw new Error(`Player initialization failed: ${errorData.error || response.statusText}`);
    }

    console.log('[INIT] Player initialized successfully:', { playerId, shipId });

    return {
      success: true,
      playerId,
      shipId,
    };
  } catch (error) {
    console.error('[INIT] Failed to initialize player:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error',
    };
  }
}

/**
 * Wipe player data only (keeps galaxy data)
 * @param playerId - Optional specific player ID to wipe. If not provided, wipes ALL player data.
 */
async function wipePlayerData(playerId?: string): Promise<void> {
  const baseUrl = DATABASE_SERVICE_URL();

  if (playerId) {
    // Delete specific player
    const response = await fetch(`${baseUrl}/admin/players/${playerId}`, {
      method: 'DELETE',
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ error: response.statusText }));
      throw new Error(`Player deletion failed: ${errorData.error || response.statusText}`);
    }
  } else {
    // Wipe all player data (legacy endpoint)
    const response = await fetch(`${baseUrl}/admin/wipe-player`, {
      method: 'POST',
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ error: response.statusText }));
      throw new Error(`Player wipe failed: ${errorData.error || response.statusText}`);
    }
  }
}

/**
 * Wipe all database data (including galaxy)
 */
async function wipeAllData(): Promise<void> {
  const baseUrl = DATABASE_SERVICE_URL();

  const response = await fetch(`${baseUrl}/admin/wipe-all`, {
    method: 'POST',
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({ error: response.statusText }));
    throw new Error(`Full wipe failed: ${errorData.error || response.statusText}`);
  }
}

/**
 * List all players
 */
export async function listPlayers(): Promise<Array<{ id: string; username: string; display_name: string; credits: number; current_system_id: string; created_at: string }>> {
  const baseUrl = DATABASE_SERVICE_URL();

  const response = await fetch(`${baseUrl}/admin/players`);

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({ error: response.statusText }));
    throw new Error(`Failed to list players: ${errorData.error || response.statusText}`);
  }

  const data = await response.json();
  return data.data || [];
}

/**
 * Delete a specific player by ID
 */
export async function deletePlayer(playerId: string): Promise<void> {
  await wipePlayerData(playerId);
}

/**
 * Seed galaxy data (systems, stations, factions, etc.)
 */
async function seedGalaxyData(): Promise<void> {
  const baseUrl = DATABASE_SERVICE_URL();

  const response = await fetch(`${baseUrl}/admin/seed`, {
    method: 'POST',
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({ error: response.statusText }));
    throw new Error(`Galaxy seed failed: ${errorData.error || response.statusText}`);
  }
}

/**
 * Start a new game (wipe player data only, keep galaxy)
 * @param playerId - Optional specific player ID to reset. If not provided, creates a new player.
 * @param displayName - Optional custom display name for the player
 */
export async function startNewGame(playerId?: string, displayName?: string): Promise<InitializationResult> {
  console.log('[GAME] Starting new game...', { playerId, displayName });

  try {
    if (playerId) {
      // Wipe specific player and reinitialize
      await wipePlayerData(playerId);
    }

    // Initialize new player with fresh UUIDs
    return await initializeNewPlayer(displayName);
  } catch (error) {
    console.error('[GAME] New game failed:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error',
    };
  }
}

/**
 * Full reset (wipe everything, reseed galaxy, create new player)
 */
export async function fullReset(): Promise<InitializationResult> {
  console.log('[GAME] Full reset (wipe all + reseed)...');

  try {
    // Step 1: Wipe everything
    await wipeAllData();

    // Step 2: Reseed galaxy data
    await seedGalaxyData();

    // Step 3: Initialize new player with fresh UUIDs
    return await initializeNewPlayer();
  } catch (error) {
    console.error('[GAME] Full reset failed:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error',
    };
  }
}
